import React, { useState, useCallback, useMemo } from 'react';
import { Trash2 } from 'lucide-react';
import localApiAdapter from '../../core/services/localApiAdapter';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import {
  QawaemTableStickyActionsTd,
  QawaemTableStickyActionsFooterTd,
  mergeStickyActionsClass,
  mergeStickySelectClass,
} from '../../products/saudi/components/QawaemTableStickyActions';
import {
  sliceQawaemStats,
  QAWAEM_KPI_GRID_CLASS,
} from '../../products/saudi/components/QawaemErpListPageShell';

/** PUT handler for QawaemClickableStatusBadge onChange on articles list pages. */
export function createArticleStatusChangeHandler(apiBase, onSuccess, field = 'status') {
  return async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`${apiBase}/${recordId}`, { [field]: newStatus });
      onSuccess?.();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };
}

export { sliceQawaemStats, QAWAEM_KPI_GRID_CLASS };

/** Create/edit modal field grid — 3 columns on md+ (Products.js / VariantesPage pattern). */
export const ARTICLES_FORM_GRID = 'grid grid-cols-1 md:grid-cols-3 gap-4';

export {
  QawaemTableStickyActionsTd,
  QawaemTableStickyActionsFooterTd,
  mergeStickyActionsClass,
  mergeStickySelectClass,
};

/** Actions column header class — matches Qawaem Console pinned إجراءات column. */
export const QAWAEM_TABLE_ACTIONS_TH_CLASS = mergeStickyActionsClass(
  'px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle',
);

/** Bulk delete button for FacturationListPageLayout `filterActions`. */
export function buildBulkDeleteFilterAction({ count, onClick, label, title }) {
  if (!count) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
      title={title}
    >
      <Trash2 size={16} />
      <span>{label}</span>
    </button>
  );
}

/** @deprecated Prefer useQawaemFilteredRowSelection from QawaemTableSelection */
export function useFilteredListRowSelection(filteredItems, getId = (item) => item.id) {
  return useQawaemFilteredRowSelection(filteredItems, getId);
}

export {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
};

/** Cap articles KPI/stat card entries to max 4 (matches inventaire list pages). */
export function mapArticlesStatCards(entries = [], max = 4) {
  return sliceQawaemStats(entries, max).map((stat, i) => ({
    key: stat.key || stat.label || stat.title || i,
    label: stat.label || stat.title,
    value: stat.value,
    icon: stat.icon,
    variant: stat.variant || (i % 2 === 0 ? 'copper' : 'forest'),
    className: stat.className,
  }));
}

/** Period dropdown labels from page `p()` helper. */
export function buildArticlesPeriodLabels(p, t) {
  return {
    label: p('period', { defaultValue: t('common.period', { defaultValue: 'الفترة' }) }),
    startLabel: p('start_date', { defaultValue: t('common.start_date', { defaultValue: 'تاريخ البداية' }) }),
    endLabel: p('end_date', { defaultValue: t('common.end_date', { defaultValue: 'تاريخ النهاية' }) }),
    resetLabel: p('reset', { defaultValue: t('common.reset', { defaultValue: 'إعادة تعيين' }) }),
    applyLabel: p('apply', { defaultValue: t('common.apply', { defaultValue: 'تطبيق' }) }),
    selectPlaceholder: p('select', { defaultValue: t('common.select', { defaultValue: 'اختر' }) }),
  };
}

/** Status filter options shared across articles list pages. */
export function buildArticlesStatusFilterOptions(p, t) {
  return [
    { value: 'all', label: p('filter_all', { defaultValue: t('common.all', { defaultValue: 'الكل' }) }) },
    { value: 'actif', label: p('filter_active', { defaultValue: t('common.active', { defaultValue: 'نشط' }) }) },
    { value: 'inactif', label: p('filter_inactive', { defaultValue: t('common.inactive', { defaultValue: 'غير نشط' }) }) },
  ];
}

/** Map badge/select status options to column header filter shape. */
export function mapStatusOptionsToColumnFilter(options = []) {
  return options.map(({ value, label }) => ({ value, label }));
}

export function articlesStatusFilterLabel(value, options) {
  return options.find((o) => o.value === value)?.label || options[0]?.label || '';
}

const articlesImportExportNoop = () => {};

/** Import/export toolbar props — pass `handlers` when the page supports I/O. */
export function buildArticlesImportExportProps({
  showImportDropdown,
  setShowImportDropdown,
  showExportDropdown,
  setShowExportDropdown,
  importDropdownRef,
  exportDropdownRef,
  p,
  t,
  handlers = {},
}) {
  return {
    importOpen: showImportDropdown,
    exportOpen: showExportDropdown,
    onToggleImport: () => {
      setShowImportDropdown(!showImportDropdown);
      setShowExportDropdown(false);
    },
    onToggleExport: () => {
      setShowExportDropdown(!showExportDropdown);
      setShowImportDropdown(false);
    },
    importRef: importDropdownRef,
    exportRef: exportDropdownRef,
    importLabel: p('import', { defaultValue: t('common.import', { defaultValue: 'استيراد' }) }),
    exportLabel: t('common.export', { defaultValue: 'تصدير' }),
    generatingLabel: p('generating', { defaultValue: t('common.loading', { defaultValue: 'جاري الإنشاء...' }) }),
    onImportCSV: handlers.onImportCSV || articlesImportExportNoop,
    onImportExcel: handlers.onImportExcel || articlesImportExportNoop,
    onImportJSON: handlers.onImportJSON || articlesImportExportNoop,
    onExportCSV: handlers.onExportCSV || articlesImportExportNoop,
    onExportExcel: handlers.onExportExcel || articlesImportExportNoop,
    onExportJSON: handlers.onExportJSON || articlesImportExportNoop,
    showJsonImportExport: handlers.showJsonImportExport ?? false,
  };
}

/** @deprecated Use buildArticlesImportExportProps without handlers */
export function buildArticlesImportExportStub(args) {
  return buildArticlesImportExportProps(args);
}

/** Column visibility for QawaemTableHeaderActionsCell on articles list tables. */
export function useQawaemColumnVisibility(keys = []) {
  const [columnVisibility, setColumnVisibility] = useState(() =>
    Object.fromEntries(keys.map((key) => [key, true])),
  );
  const handleToggleColumn = useCallback((key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);
  const showCol = useCallback((key) => columnVisibility[key] !== false, [columnVisibility]);
  const visibleDataColCount = keys.filter((k) => columnVisibility[k] !== false).length;
  return { columnVisibility, handleToggleColumn, showCol, visibleDataColCount };
}

/** Cap list rows to configured itemsPerPage. */
export function sliceArticlesListItems(items = [], itemsPerPage = 50) {
  const limit = Math.max(1, parseInt(itemsPerPage, 10) || 50);
  return items.slice(0, limit);
}

/** Shown when a submodule is turned off in إعدادات الأصناف. */
export function ArticlesConfigNotice({ message }) {
  if (!message) return null;
  return (
    <div className="mb-4 rounded-2xl border border-amber-500/35 bg-amber-500/12 px-4 py-3 text-amber-100 text-sm text-right leading-relaxed">
      {message}
    </div>
  );
}
